/**
 * IndexedDB Service for Offline Data Storage
 *
 * This service provides a robust offline-first data layer using IndexedDB.
 * It stores cached API responses, pending mutations, and user preferences
 * for seamless offline operation.
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Database schema definition
interface OHCSDBSchema extends DBSchema {
  // Cached API responses
  apiCache: {
    key: string;
    value: {
      url: string;
      data: unknown;
      timestamp: number;
      etag?: string;
      expiresAt?: number;
    };
    indexes: {
      'by-timestamp': number;
      'by-expires': number;
    };
  };

  // Pending sync queue for offline mutations
  syncQueue: {
    key: number;
    value: {
      id?: number;
      type: 'create' | 'update' | 'delete';
      endpoint: string;
      method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      payload?: unknown;
      headers?: Record<string, string>;
      createdAt: number;
      retryCount: number;
      lastError?: string;
      priority: number;
    };
    indexes: {
      'by-created': number;
      'by-priority': number;
    };
  };

  // Offline user data cache
  userData: {
    key: string;
    value: {
      id: string;
      type: 'user' | 'pfile' | 'document' | 'jacket' | 'mda' | 'department';
      data: unknown;
      syncedAt: number;
      isDirty: boolean;
    };
    indexes: {
      'by-type': string;
      'by-synced': number;
      'by-dirty': number;
    };
  };

  // Downloaded documents for offline access
  offlineDocuments: {
    key: string;
    value: {
      documentId: string;
      name: string;
      mimeType: string;
      blob: Blob;
      downloadedAt: number;
      size: number;
      pfileId?: string;
    };
    indexes: {
      'by-downloaded': number;
      'by-pfile': string;
    };
  };

  // User preferences and settings
  preferences: {
    key: string;
    value: unknown;
  };

  // Session data
  session: {
    key: string;
    value: {
      accessToken?: string;
      refreshToken?: string;
      user?: unknown;
      mda?: unknown;
      expiresAt?: number;
    };
  };
}

const DB_NAME = 'ohcs-offline-db';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<OHCSDBSchema> | null = null;

/**
 * Initialize and get the database instance
 */
export async function getDB(): Promise<IDBPDatabase<OHCSDBSchema>> {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = await openDB<OHCSDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion) {
      console.log(`[OfflineDB] Upgrading from v${oldVersion} to v${newVersion}`);

      // API Cache store
      if (!db.objectStoreNames.contains('apiCache')) {
        const apiCacheStore = db.createObjectStore('apiCache', { keyPath: 'url' });
        apiCacheStore.createIndex('by-timestamp', 'timestamp');
        apiCacheStore.createIndex('by-expires', 'expiresAt');
      }

      // Sync Queue store
      if (!db.objectStoreNames.contains('syncQueue')) {
        const syncQueueStore = db.createObjectStore('syncQueue', {
          keyPath: 'id',
          autoIncrement: true,
        });
        syncQueueStore.createIndex('by-created', 'createdAt');
        syncQueueStore.createIndex('by-priority', 'priority');
      }

      // User Data store
      if (!db.objectStoreNames.contains('userData')) {
        const userDataStore = db.createObjectStore('userData', { keyPath: 'id' });
        userDataStore.createIndex('by-type', 'type');
        userDataStore.createIndex('by-synced', 'syncedAt');
        userDataStore.createIndex('by-dirty', 'isDirty');
      }

      // Offline Documents store
      if (!db.objectStoreNames.contains('offlineDocuments')) {
        const offlineDocsStore = db.createObjectStore('offlineDocuments', {
          keyPath: 'documentId',
        });
        offlineDocsStore.createIndex('by-downloaded', 'downloadedAt');
        offlineDocsStore.createIndex('by-pfile', 'pfileId');
      }

      // Preferences store
      if (!db.objectStoreNames.contains('preferences')) {
        db.createObjectStore('preferences');
      }

      // Session store
      if (!db.objectStoreNames.contains('session')) {
        db.createObjectStore('session');
      }
    },
    blocked() {
      console.warn('[OfflineDB] Database upgrade blocked by other tabs');
    },
    blocking() {
      console.warn('[OfflineDB] This tab is blocking database upgrade');
      dbInstance?.close();
      dbInstance = null;
    },
    terminated() {
      console.error('[OfflineDB] Database connection terminated unexpectedly');
      dbInstance = null;
    },
  });

  return dbInstance;
}

/**
 * API Cache Operations
 */
export const apiCache = {
  async get<T>(url: string): Promise<T | null> {
    const db = await getDB();
    const cached = await db.get('apiCache', url);

    if (!cached) return null;

    // Check if expired
    if (cached.expiresAt && cached.expiresAt < Date.now()) {
      await db.delete('apiCache', url);
      return null;
    }

    return cached.data as T;
  },

  async set(url: string, data: unknown, ttlSeconds: number = 3600): Promise<void> {
    const db = await getDB();
    await db.put('apiCache', {
      url,
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttlSeconds * 1000,
    });
  },

  async delete(url: string): Promise<void> {
    const db = await getDB();
    await db.delete('apiCache', url);
  },

  async clear(): Promise<void> {
    const db = await getDB();
    await db.clear('apiCache');
  },

  async clearExpired(): Promise<number> {
    const db = await getDB();
    const tx = db.transaction('apiCache', 'readwrite');
    const store = tx.objectStore('apiCache');
    const index = store.index('by-expires');
    const now = Date.now();
    let count = 0;

    let cursor = await index.openCursor(IDBKeyRange.upperBound(now));
    while (cursor) {
      await cursor.delete();
      count++;
      cursor = await cursor.continue();
    }

    await tx.done;
    return count;
  },
};

/**
 * Sync Queue Operations - for offline mutations
 */
export const syncQueue = {
  async add(item: Omit<OHCSDBSchema['syncQueue']['value'], 'id' | 'createdAt' | 'retryCount'>): Promise<number> {
    const db = await getDB();
    const id = await db.add('syncQueue', {
      ...item,
      createdAt: Date.now(),
      retryCount: 0,
      priority: item.priority ?? 1,
    });
    return id as number;
  },

  async getAll(): Promise<OHCSDBSchema['syncQueue']['value'][]> {
    const db = await getDB();
    return db.getAllFromIndex('syncQueue', 'by-priority');
  },

  async getPending(): Promise<OHCSDBSchema['syncQueue']['value'][]> {
    const db = await getDB();
    const all = await db.getAllFromIndex('syncQueue', 'by-priority');
    return all.filter(item => item.retryCount < 5);
  },

  async updateRetry(id: number, error: string): Promise<void> {
    const db = await getDB();
    const item = await db.get('syncQueue', id);
    if (item) {
      item.retryCount++;
      item.lastError = error;
      await db.put('syncQueue', item);
    }
  },

  async remove(id: number): Promise<void> {
    const db = await getDB();
    await db.delete('syncQueue', id);
  },

  async clear(): Promise<void> {
    const db = await getDB();
    await db.clear('syncQueue');
  },

  async count(): Promise<number> {
    const db = await getDB();
    return db.count('syncQueue');
  },
};

/**
 * User Data Cache Operations
 */
export const userDataCache = {
  async get<T>(id: string): Promise<T | null> {
    const db = await getDB();
    const data = await db.get('userData', id);
    return data?.data as T | null;
  },

  async set(id: string, type: OHCSDBSchema['userData']['value']['type'], data: unknown): Promise<void> {
    const db = await getDB();
    await db.put('userData', {
      id,
      type,
      data,
      syncedAt: Date.now(),
      isDirty: false,
    });
  },

  async setDirty(id: string, data: unknown): Promise<void> {
    const db = await getDB();
    const existing = await db.get('userData', id);
    if (existing) {
      existing.data = data;
      existing.isDirty = true;
      await db.put('userData', existing);
    }
  },

  async getByType<T>(type: OHCSDBSchema['userData']['value']['type']): Promise<T[]> {
    const db = await getDB();
    const items = await db.getAllFromIndex('userData', 'by-type', type);
    return items.map(item => item.data as T);
  },

  async getDirty(): Promise<OHCSDBSchema['userData']['value'][]> {
    const db = await getDB();
    const all = await db.getAll('userData');
    return all.filter(item => item.isDirty);
  },

  async markSynced(id: string): Promise<void> {
    const db = await getDB();
    const item = await db.get('userData', id);
    if (item) {
      item.isDirty = false;
      item.syncedAt = Date.now();
      await db.put('userData', item);
    }
  },

  async delete(id: string): Promise<void> {
    const db = await getDB();
    await db.delete('userData', id);
  },

  async clear(): Promise<void> {
    const db = await getDB();
    await db.clear('userData');
  },
};

/**
 * Offline Documents Operations
 */
export const offlineDocuments = {
  async save(document: Omit<OHCSDBSchema['offlineDocuments']['value'], 'downloadedAt'>): Promise<void> {
    const db = await getDB();
    await db.put('offlineDocuments', {
      ...document,
      downloadedAt: Date.now(),
    });
  },

  async get(documentId: string): Promise<OHCSDBSchema['offlineDocuments']['value'] | null> {
    const db = await getDB();
    const doc = await db.get('offlineDocuments', documentId);
    return doc ?? null;
  },

  async getByPFile(pfileId: string): Promise<OHCSDBSchema['offlineDocuments']['value'][]> {
    const db = await getDB();
    return db.getAllFromIndex('offlineDocuments', 'by-pfile', pfileId);
  },

  async getAll(): Promise<OHCSDBSchema['offlineDocuments']['value'][]> {
    const db = await getDB();
    return db.getAll('offlineDocuments');
  },

  async delete(documentId: string): Promise<void> {
    const db = await getDB();
    await db.delete('offlineDocuments', documentId);
  },

  async clear(): Promise<void> {
    const db = await getDB();
    await db.clear('offlineDocuments');
  },

  async getTotalSize(): Promise<number> {
    const db = await getDB();
    const docs = await db.getAll('offlineDocuments');
    return docs.reduce((sum, doc) => sum + doc.size, 0);
  },
};

/**
 * Preferences Operations
 */
export const preferences = {
  async get<T>(key: string, defaultValue?: T): Promise<T | undefined> {
    const db = await getDB();
    const value = await db.get('preferences', key);
    return (value as T) ?? defaultValue;
  },

  async set(key: string, value: unknown): Promise<void> {
    const db = await getDB();
    await db.put('preferences', value, key);
  },

  async delete(key: string): Promise<void> {
    const db = await getDB();
    await db.delete('preferences', key);
  },
};

/**
 * Session Operations
 */
export const sessionStore = {
  async get(): Promise<OHCSDBSchema['session']['value'] | null> {
    const db = await getDB();
    const session = await db.get('session', 'current');
    return session ?? null;
  },

  async set(session: OHCSDBSchema['session']['value']): Promise<void> {
    const db = await getDB();
    await db.put('session', session, 'current');
  },

  async clear(): Promise<void> {
    const db = await getDB();
    await db.delete('session', 'current');
  },
};

/**
 * Database maintenance utilities
 */
export const dbMaintenance = {
  async getStorageEstimate(): Promise<{ usage: number; quota: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage ?? 0,
        quota: estimate.quota ?? 0,
      };
    }
    return { usage: 0, quota: 0 };
  },

  async cleanup(): Promise<{ expiredCache: number; failedSync: number }> {
    // Clear expired cache
    const expiredCache = await apiCache.clearExpired();

    // Remove failed sync items (retry count >= 5)
    const db = await getDB();
    const syncItems = await db.getAll('syncQueue');
    let failedSync = 0;
    for (const item of syncItems) {
      if (item.retryCount >= 5) {
        await db.delete('syncQueue', item.id!);
        failedSync++;
      }
    }

    return { expiredCache, failedSync };
  },

  async exportData(): Promise<string> {
    const db = await getDB();
    const data = {
      apiCache: await db.getAll('apiCache'),
      syncQueue: await db.getAll('syncQueue'),
      userData: await db.getAll('userData'),
      preferences: await db.getAll('preferences'),
    };
    return JSON.stringify(data, null, 2);
  },
};
